import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../context/OrgContext';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/apiService';

const CreateOrganization: React.FC = () => {
  const [organizationName, setOrganizationName] = useState('');
  const [urlName, setUrlName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [urlAvailable, setUrlAvailable] = useState<boolean | null>(null);
  const [checkingUrl, setCheckingUrl] = useState(false);
  
  const { createOrganization, organizations, maxOrganizations, canCreateMore } = useOrg();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Auto-generate URL name when organization name changes
  useEffect(() => {
    if (organizationName.trim()) {
      const generatedUrl = apiService.generateUrlName(organizationName);
      setUrlName(generatedUrl);
    }
  }, [organizationName]);

  // Check URL availability when URL name changes
  useEffect(() => {
    const checkAvailability = async () => {
      if (!urlName.trim() || !apiService.isValidUrlName(urlName)) {
        setUrlAvailable(null);
        return;
      }

      setCheckingUrl(true);
      try {
        const response = await apiService.checkUrlAvailability(urlName);
        setUrlAvailable(response.data?.available || false);
      } catch (error) {
        console.error('Failed to check URL availability:', error);
        setUrlAvailable(null);
      } finally {
        setCheckingUrl(false);
      }
    };

    const timeoutId = setTimeout(checkAvailability, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [urlName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!organizationName.trim() || !urlName.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (!canCreateMore) {
      setError(`You can create a maximum of ${maxOrganizations} cafe${maxOrganizations > 1 ? 's' : ''}. Please contact us for more information.`);
      // Redirect to contact page after a short delay
      setTimeout(() => {
        navigate('/contact');
      }, 3000);
      return;
    }

    if (!apiService.isValidUrlName(urlName)) {
      setError('URL name can only contain lowercase letters, numbers, and hyphens');
      return;
    }

    if (urlAvailable === false) {
      setError('This URL name is already taken');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const organization = await createOrganization(organizationName.trim(), urlName.trim());
      
      if (organization) {
        // Redirect to the new organization
        navigate(`/${organization.urlName}`, { replace: true });
      }
    } catch (error: any) {
      setError(error.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const getUrlStatus = () => {
    if (!urlName.trim()) return null;
    if (!apiService.isValidUrlName(urlName)) {
      return { type: 'error', message: 'Only lowercase letters, numbers, and hyphens allowed' };
    }
    if (checkingUrl) {
      return { type: 'checking', message: 'Checking availability...' };
    }
    if (urlAvailable === true) {
      return { type: 'success', message: 'Available!' };
    }
    if (urlAvailable === false) {
      return { type: 'error', message: 'Already taken' };
    }
    return null;
  };

  const urlStatus = getUrlStatus();

  return (
    <div className="create-org-container">
      <div className="create-org-card">
        {/* Header */}
        <div className="create-org-header">
          <div className="header-content">
            <h1>Welcome to BeanBuzz</h1>
            <p>Let's create your first cafe to get started</p>
          </div>
          <div className="header-actions">
            <button 
              onClick={handleLogout}
              className="btn btn-secondary btn-small"
            >
              Logout ({currentUser?.email?.split('@')[0]})
            </button>
          </div>
        </div>

        {/* Main Form */}
        <div className="create-org-form-section">
          <div className="form-intro">
            <h2>Create Your Cafe</h2>
            <p>Each cafe gets its own unique URL where customers can browse your menu and place orders. You can create up to {maxOrganizations} cafe{maxOrganizations > 1 ? 's' : ''}.</p>
            {organizations.length > 0 && (
              <div className="cafe-limit-info">
                                <p>You have {organizations.length} of {maxOrganizations} cafe{maxOrganizations > 1 ? 's' : ''} created.</p>
                {!canCreateMore && (
                  <div className="limit-warning">
                    <p>
                      You've reached the maximum of {maxOrganizations} cafe{maxOrganizations > 1 ? 's' : ''}.
                    </p>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                      Need more cafes? <a href="/contact" style={{ color: '#007AFF', textDecoration: 'underline' }}>Contact us</a> to discuss your needs.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="create-org-form">
            <div className="form-group">
              <label htmlFor="organizationName">Cafe Name</label>
              <input
                type="text"
                id="organizationName"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="e.g., Bean & Brew Downtown"
                required
                disabled={loading}
              />
              <small>This is the name your customers will see</small>
            </div>

            <div className="form-group">
              <label htmlFor="urlName">Cafe URL (Permanent)</label>
              <div className="url-input-wrapper">
                <span className="url-prefix">beanbuzz.app/</span>
                <input
                  type="text"
                  id="urlName"
                  value={urlName}
                  onChange={(e) => setUrlName(e.target.value.toLowerCase())}
                  placeholder="bean-brew-downtown"
                  pattern="[a-z0-9\-]+"
                  required
                  disabled={loading}
                />
              </div>
              {urlStatus && (
                <div className={`url-status ${urlStatus.type}`}>
                  {urlStatus.type === 'success' && '✓ '}
                  {urlStatus.type === 'error' && '✗ '}
                  {urlStatus.type === 'checking' && '🔄 '}
                  {urlStatus.message}
                </div>
              )}
              <small>⚠️ This URL cannot be changed once created. Your customers will use this link to order from your cafe.</small>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-large create-org-btn"
              disabled={loading || urlAvailable === false || checkingUrl || !canCreateMore}
            >
              {!canCreateMore 
                ? `Maximum ${maxOrganizations} Cafe${maxOrganizations > 1 ? 's' : ''} Reached`
                : loading 
                  ? 'Creating Cafe...' 
                  : 'Create Cafe'
              }
            </button>
          </form>

          {/* Info Section */}
          <div className="create-org-info">
            <h3>What happens next?</h3>
            <div className="info-steps">
              <div className="info-step">
                <div className="step-icon">☕</div>
                <div className="step-content">
                  <h4>Cafe Created</h4>
                  <p>Your cafe will be set up and ready for customers</p>
                </div>
              </div>
              <div className="info-step">
                <div className="step-icon">📋</div>
                <div className="step-content">
                  <h4>Add Your Menu</h4>
                  <p>Upload menu items with prices and descriptions</p>
                </div>
              </div>
              <div className="info-step">
                <div className="step-icon">📱</div>
                <div className="step-content">
                  <h4>Start Taking Orders</h4>
                  <p>Share your cafe URL and begin receiving orders</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization; 